import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Calculator } from "lucide-react";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { SUPPORTED_CURRENCIES } from "../lib/money";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

// Same metric keys the backend validator accepts. Keep this in sync with
// METRIC_KEYS in /app/backend/enrichment/financials.py.
// `unit: "money"` is rendered using the currency the user selected at the
// top of the form. "%" is rendered as a percentage.
// Labels are resolved at render time from components:enrichment.financials.metrics.<key>
// (shared with FinancialsEnrichmentPanel.jsx). `group` is a stable identifier, not display text.
const METRIC_FIELDS = [
  { key: "annual_revenue", unit: "money", group: "pl" },
  { key: "monthly_revenue", unit: "money", group: "pl" },
  { key: "annual_ebitda", unit: "money", group: "pl" },
  { key: "gross_margin_pct", unit: "%", group: "pl" },
  { key: "operating_margin_pct", unit: "%", group: "pl" },

  { key: "monthly_rent", unit: "money", group: "cash_flow" },
  { key: "annual_debt_service", unit: "money", group: "cash_flow" },
  { key: "monthly_debt_payments", unit: "money", group: "cash_flow" },
  { key: "cash_position", unit: "money", group: "cash_flow" },

  { key: "current_assets", unit: "money", group: "balance_sheet" },
  { key: "current_liabilities", unit: "money", group: "balance_sheet" },
  { key: "total_assets", unit: "money", group: "balance_sheet" },
  { key: "total_liabilities", unit: "money", group: "balance_sheet" },
  { key: "tangible_net_worth", unit: "money", group: "balance_sheet" },
];

const GROUPS = ["pl", "cash_flow", "balance_sheet"];

export default function ManualFinancials() {
  const { t } = useTranslation(["manual_financials", "components", "common"]);
  const navigate = useNavigate();
  const [periodLabel, setPeriodLabel] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [summary, setSummary] = useState("");
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const setMetric = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));

  const parseNumber = (s) => {
    if (s == null || s === "") return null;
    const cleaned = String(s).replace(/[,$\s%]/g, "").trim();
    const n = Number(cleaned);
    return isFinite(n) ? n : null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const metrics = {};
    let count = 0;
    for (const f of METRIC_FIELDS) {
      const n = parseNumber(values[f.key]);
      if (n !== null) {
        metrics[f.key] = n;
        count += 1;
      }
    }
    if (count === 0) {
      toast.error(t("manual_financials:toast_min_metric"));
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/financials/manual", {
        period_label: periodLabel || null,
        period_start: periodStart || null,
        period_end: periodEnd || null,
        currency,
        metrics,
        summary: summary || null,
      });
      toast.success(t("manual_financials:toast_saved"));
      navigate(`/analysis/${res.data.id}`);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || t("manual_financials:toast_save_failed");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="animate-fade-up max-w-4xl" data-testid="manual-financials-page">
      <button
        onClick={() => navigate(-1)}
        className="text-xs text-zinc-500 hover:text-zinc-950 inline-flex items-center gap-1 mb-4"
        data-testid="manual-fin-back"
      >
        <ArrowLeft size={12} /> {t("common:actions.back")}
      </button>

      <div className="flex items-start gap-3 mb-2">
        <div className="w-9 h-9 rounded-lg grid place-items-center bg-indigo-50 text-indigo-700">
          <Calculator size={16} strokeWidth={1.75} />
        </div>
        <div>
          <h1 className="font-display font-semibold text-2xl tracking-tight">
            {t("manual_financials:title")}
          </h1>
          <p className="text-sm text-zinc-500 mt-0.5">
            {t("manual_financials:description")}
          </p>
        </div>
      </div>

      <form
        onSubmit={onSubmit}
        className="mt-8 bg-white border border-zinc-200 rounded-xl p-6 space-y-8"
        data-testid="manual-fin-form"
      >
        {/* Period + Currency */}
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-3">{t("manual_financials:period_currency_label")}</p>
          <div className="grid sm:grid-cols-4 gap-3">
            <Field
              label={t("manual_financials:period_label_field")}
              placeholder={t("manual_financials:period_label_placeholder")}
              value={periodLabel}
              onChange={setPeriodLabel}
              testid="manual-fin-period-label"
            />
            <Field
              label={t("manual_financials:start")}
              type="date"
              value={periodStart}
              onChange={setPeriodStart}
              testid="manual-fin-period-start"
            />
            <Field
              label={t("manual_financials:end")}
              type="date"
              value={periodEnd}
              onChange={setPeriodEnd}
              testid="manual-fin-period-end"
            />
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1 inline-flex items-center gap-1">
                {t("manual_financials:currency")} <span className="text-zinc-400 normal-case tracking-normal">{t("manual_financials:currency_preserved")}</span>
              </span>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                data-testid="manual-fin-currency"
                className="w-full text-sm border border-zinc-200 rounded-md px-3 py-2 focus:outline-none focus:border-zinc-950 bg-white"
              >
                {SUPPORTED_CURRENCIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>
          </div>
          <p className="text-[11px] text-zinc-500 mt-2">
            {t("manual_financials:currency_note")}
          </p>
        </div>

        {GROUPS.map((group) => (
          <div key={group}>
            <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-3">{t(`manual_financials:groups.${group}`)}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {METRIC_FIELDS.filter((f) => f.group === group).map((f) => (
                <Field
                  key={f.key}
                  label={t(`components:enrichment.financials.metrics.${f.key}`)}
                  unit={f.unit === "money" ? currency : f.unit}
                  value={values[f.key] ?? ""}
                  onChange={(v) => setMetric(f.key, v)}
                  testid={`manual-fin-${f.key}`}
                  inputMode="decimal"
                />
              ))}
            </div>
          </div>
        ))}

        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-3">
            {t("manual_financials:summary_label")}
          </p>
          <textarea
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder={t("manual_financials:summary_placeholder")}
            className="w-full text-sm border border-zinc-200 rounded-md px-3 py-2 focus:outline-none focus:border-zinc-950 min-h-[80px]"
            data-testid="manual-fin-summary"
          />
        </div>

        <div className="flex items-center gap-3 pt-2 border-t border-zinc-100">
          <Button
            type="submit"
            disabled={submitting}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="manual-fin-submit"
          >
            <Save size={14} className="mr-1" />
            {submitting ? t("manual_financials:saving") : t("manual_financials:save_button")}
          </Button>
          <p className="text-xs text-zinc-500">
            {t("manual_financials:footer_note")}
          </p>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, unit, testid, inputMode }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1 inline-flex items-center gap-1">
        {label} {unit && <span className="text-zinc-400 normal-case tracking-normal">({unit})</span>}
      </span>
      <input
        type={type}
        inputMode={inputMode}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full text-sm border border-zinc-200 rounded-md px-3 py-2 focus:outline-none focus:border-zinc-950 tabular-nums"
      />
    </label>
  );
}
